/**
 * ContentPublicationFirestoreService — CRUD + subscriptions for
 *   projects/{pid}/content_strategies/{sid}/publications/{pubId}
 *
 * Design notes:
 *   - Firestore-direct (no backend endpoint), mirroring the TopicBacklog /
 *     ThemeCatalog / PostExamples precedents.
 *   - Uses `updateDoc` with dot-notation for partial updates — per project
 *     memory (feedback_firestore_merge_bug), setDoc merge:true leaks stale
 *     nested keys.
 *   - Every write funnels through stripUndefined (Sprint 16 bug A7).
 *   - `subscribeForWindow` fans out one listener per strategy and merges
 *     the results on the client. This matches the cross-plan mode's
 *     contract and keeps Firestore queries simple (no cross-path joins).
 */

import {
  collection, doc, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot,
  query, where, orderBy, serverTimestamp, writeBatch,
  type Unsubscribe, type Timestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ContentPublication } from '../types';
import { stripUndefined } from '../utils/stripUndefined';

function pubId(): string {
  return `pub_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Iterable window filter — pubs inside [start, end] by scheduledAt. */
function inWindow(pub: ContentPublication, start: Date, end: Date): boolean {
  const t = new Date(pub.scheduledAt).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

export class ContentPublicationFirestoreService {
  private readonly db = CoreFirebaseService.getInstance().firestore;

  // ── Paths ──────────────────────────────────────────────────────────

  private publicationsCollection(projectId: string, strategyId: string) {
    return collection(
      this.db, 'projects', projectId,
      'content_strategies', strategyId, 'publications',
    );
  }

  private publicationDoc(projectId: string, strategyId: string, id: string) {
    return doc(
      this.db, 'projects', projectId,
      'content_strategies', strategyId, 'publications', id,
    );
  }

  // ── Read ───────────────────────────────────────────────────────────

  async list(projectId: string, strategyId: string): Promise<ContentPublication[]> {
    const snap = await getDocs(this.publicationsCollection(projectId, strategyId));
    return snap.docs.map((d) => d.data() as ContentPublication);
  }

  subscribe(
    projectId: string,
    strategyId: string,
    callback: (pubs: ContentPublication[]) => void,
  ): Unsubscribe {
    const q = query(this.publicationsCollection(projectId, strategyId), orderBy('scheduledAt', 'asc'));
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => d.data() as ContentPublication));
    }, (err) => {
      // eslint-disable-next-line no-console
      console.warn('[ContentPublicationFirestoreService.subscribe] error', err);
      callback([]);
    });
  }

  /**
   * Multi-strategy window subscription. Returns a single unsubscribe that
   * tears down every inner listener. The caller receives the MERGED list
   * on each inner emission — that's the contract cross-plan mode needs.
   *
   * If any single strategy listener fails, the rest keep running and the
   * merged callback fires with whatever data is available.
   */
  subscribeForWindow(
    projectId: string,
    strategyIds: string[],
    window: { start: Date; end: Date },
    callback: (pubs: ContentPublication[]) => void,
  ): Unsubscribe {
    const snapshots = new Map<string, ContentPublication[]>();
    const emit = () => {
      const merged: ContentPublication[] = [];
      for (const list of snapshots.values()) {
        for (const p of list) {
          if (inWindow(p, window.start, window.end)) merged.push(p);
        }
      }
      merged.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
      callback(merged);
    };

    const unsubs: Unsubscribe[] = strategyIds.map((sid) =>
      onSnapshot(
        query(this.publicationsCollection(projectId, sid), orderBy('scheduledAt', 'asc')),
        (snap) => {
          snapshots.set(sid, snap.docs.map((d) => d.data() as ContentPublication));
          emit();
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.warn(`[ContentPublicationFirestoreService.subscribeForWindow:${sid}] error`, err);
          snapshots.set(sid, []);
          emit();
        },
      ),
    );

    return () => {
      for (const u of unsubs) {
        try { u(); } catch { /* swallow — teardown should never throw */ }
      }
    };
  }

  // ── Write ──────────────────────────────────────────────────────────

  /**
   * Create a publication. Short-circuits when a publication already
   * exists for the given contentPostId — keeps the migration idempotent.
   */
  async create(
    projectId: string,
    strategyId: string,
    data: Omit<ContentPublication, 'id' | 'createdAt' | 'updatedAt'> & { id?: string },
  ): Promise<string> {
    const id = data.id ?? pubId();
    const payload = stripUndefined({
      ...data,
      id,
      projectId,
      strategyId,
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    });
    await setDoc(this.publicationDoc(projectId, strategyId, id), payload);
    return id;
  }

  async update(
    projectId: string,
    strategyId: string,
    pubIdValue: string,
    patch: Partial<Omit<ContentPublication, 'id' | 'projectId' | 'strategyId'>>,
  ): Promise<void> {
    const clean = stripUndefined(patch);
    await updateDoc(this.publicationDoc(projectId, strategyId, pubIdValue), {
      ...clean,
      updatedAt: serverTimestamp(),
    });
  }

  /** Move a publication to a different day / time. */
  async move(
    projectId: string,
    strategyId: string,
    pubIdValue: string,
    move: Pick<ContentPublication, 'scheduledAt' | 'weekIndex' | 'weekdayIndex'>,
  ): Promise<void> {
    await this.update(projectId, strategyId, pubIdValue, move);
  }

  async delete(projectId: string, strategyId: string, pubIdValue: string): Promise<void> {
    await deleteDoc(this.publicationDoc(projectId, strategyId, pubIdValue));
  }

  /**
   * Bulk-insert a batch of publications. Used by backfillFromPosts after
   * the pure util derives the diff. Caps at ~400 per batch to stay under
   * the Firestore 500-op batch limit.
   */
  async bulkCreate(
    projectId: string,
    strategyId: string,
    publications: ContentPublication[],
  ): Promise<void> {
    const CHUNK = 400;
    for (let i = 0; i < publications.length; i += CHUNK) {
      const slice = publications.slice(i, i + CHUNK);
      const batch = writeBatch(this.db);
      for (const pub of slice) {
        const payload = stripUndefined({
          ...pub,
          createdAt: serverTimestamp() as unknown as Timestamp,
          updatedAt: serverTimestamp() as unknown as Timestamp,
        });
        batch.set(this.publicationDoc(projectId, strategyId, pub.id), payload, { merge: true });
      }
      await batch.commit();
    }
  }
}
